import { useContext } from "react"

// Context
import { UserContext } from "../../context/userContext"

//Referral components
import { getReferralsParticipantServiceStaffId } from "../../config/microservicesSystemUsers"
import { refApiRefEntityNotePut } from "./ReferralsAPI"
import { getNotePayload } from "./sharedFunctions"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"

//Formik
import { Formik, Form, useField } from "formik"
import * as Yup from "yup"
import FormikDropDown from "../../helpers/formikInputs/FormikDropDown"
import FormikTextAreaInput from "../../helpers/formikInputs/FormikTextAreaInput"

//GraphQL
import { useMutation } from "@apollo/client"
import { UPDATE_REFERRAL_Block } from "./query"
import { LogError } from "../../helpers/logger"

import styles from "../../styles/Referrals.module.css"

export default function ReferralStatusPopup({
  showForm,
  onHideForm,
  onStatusChanged,
  referralOrganizationId,
  referralIdStamp,
  referralStatus,
  referralParticipantsNotes,
  referralEntityId,
  allowStatusUpdate,
}) {
  const userCtx = useContext(UserContext)
  const [, , statusFieldHelper] = useField("referral.values.referral_status")

  const referralStatuses = [
    "Active referral",
    "Completed referral",
    "Incomplete referral",
  ]
  const [updateReferral] = useMutation(UPDATE_REFERRAL_Block, {
    variables: {
      referral_status: "",
      referral_participants_notes: "",
      user_type: "",
      idStamp: "",
    },
  })

  const validationSchema = Yup.object().shape({
    status: Yup.string()
      .required("Please specify a status to update")
      .notOneOf(
        [referralStatus],
        "The status is unchanged, please select a new status",
      ),
    note: Yup.string().required("Please enter a note for the status update"),
  })

  const handleSubmit = async (values) => {
    try {
      const serviceUserId = getReferralsParticipantServiceStaffId()
      const referral_update = await updateReferral({
        variables: {
          staff_id: serviceUserId,
          organization_id: referralOrganizationId,
          referral_status: values.status,
          referral_participants_notes: referralParticipantsNotes,
          user_type: "participant",
          idStamp: referralIdStamp,
        },
      })
      if (!!referral_update.data.updateBlockReferral) {
        //Add status update note to Referral service
        const payload = {
          userId: userCtx.participant_id,
          note: values.note,
          referralId: referralIdStamp,
          referralEntityId: referralEntityId,
          notePayload: getNotePayload(userCtx),
          ReferralStatusOld: referralStatus,
          ReferralStatusNew: values.status,
        }
        const referralNoteResult = await refApiRefEntityNotePut(
          userCtx.tokens.getMainToken(),
          values.id,
          payload,
        )

        if (!referralNoteResult) {
          LogError("Unable to submit status update note", referral_update)
          userCtx.setNotificationError("Unable to submit status update note")
        }

        statusFieldHelper.setValue(values.status)
        onStatusChanged(values.status)
      } else {
        LogError("Unable to submit status update", referral_update)
        userCtx.setNotificationError("Unable to submit status update")
        return false
      }
    } catch (error) {
      LogError("Unable to submit status update", error)
      userCtx.setNotificationError("Unable to submit status update")
      return false
    }
  }
  return (
    <Dialog
      visible={showForm}
      showHeader={false}
      onHide={() => {
        return onHideForm()
      }}
      className={styles.statusModal}
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          status: referralStatus,
          note: "",
        }}
        enableReinitialize
        onSubmit={async (values) => {
          return await handleSubmit(values)
        }}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty, values, errors }) => (
          <Form onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <label>Referral status update</label>
              <Button
                icon="pi pi-times"
                className={styles.hideModalButton}
                onClick={() => {
                  onHideForm()
                }}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.modalFormContainer}>
              <hr className={styles.modalHeaderLine} />
              <div className={styles.modalField}>
                <FormikDropDown
                  name="status"
                  label="Referral status"
                  helpText={"Please select a new status"}
                  placeholderText="Partner status"
                  options={
                    allowStatusUpdate
                      ? referralStatuses
                      : referralStatuses.filter((s) => s !== referralStatus)
                  }
                  required={true}
                  info_id={"referral_status"}
                />
              </div>
              <div className={styles.modalField}>
                <FormikTextAreaInput
                  label={"Status change note"}
                  helpText={"Please add a note for this status update. "}
                  name="note"
                  placeholderText="Note"
                  required={true}
                  rows={4}
                  info_id={"referral_status_note"}
                />
              </div>
              <div className={styles.modalFooter}>
                <Button
                  className={styles.cancelButton}
                  label="Cancel"
                  type="button"
                  onClick={() => {
                    onHideForm()
                  }}
                  disabled={isSubmitting}
                />
                <Button
                  className={styles.submitButton}
                  label="Submit"
                  icon={isSubmitting && "pi pi-spin pi-spinner"}
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
